/**
 * The section's body, behind the access the view settled on (#848): the access
 * states (loading, failed, locked) and, when it is open, the tabs with the
 * theme set's panel and filter. `lockedDemo` — the open tab is the demo of a
 * section below Pro (#2816): a banner over the tabs says so.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { VoiceThemeFilterErrorCode } from '@aica/api-types/schemas';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertClose, AlertDescription, AlertIcon } from '@/components/ui/alert';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { VoiceAudienceTab } from './VoiceAudienceTab';
import { VoiceAudienceTabList } from './VoiceAudienceTabList';
import { VoiceDemoBanner } from './VoiceDemoBanner';
import { VoiceLocked, type VoiceSectionAccess } from './VoiceLocked';
import { VoiceSalesTab } from './VoiceSalesTab';
import { VoiceSeriesScope } from './VoiceSeriesScope';
import { VoiceSiteTab } from './VoiceSiteTab';
import { VoiceLoading } from './VoiceLoading';
import { VoiceThemeFilterPicker } from './VoiceThemeFilterPicker';
import { VoiceThemesSheet } from './themes/VoiceThemesSheet';
import { VoiceThemeRefusalContext } from './voice-theme-filter-context';
import { useVoiceThemesAvailable } from './themes/useVoiceThemes';
import { useQuestionsSwitch } from './useQuestionsSwitch';
import type { VoiceOfCustomerViewProps } from './VoiceOfCustomerView';
import { isVoiceAudience, themeFilterPatch, type VoiceAudience } from './voice-search';

export function VoiceOfCustomerSection({
  search,
  onSearchChange,
  onOpenCall,
  access,
  onRetryAccess: retryAccess,
  upgradeHref,
  lockedDemo,
  sessionShell,
}: Omit<VoiceOfCustomerViewProps, 'access'> & {
  access: VoiceSectionAccess;
  lockedDemo: boolean;
  /** The shell hands the access itself (Bitrix24): the flags come from its session. */
  sessionShell: boolean;
}) {
  const { t } = useTranslation('dashboard');
  const audience: VoiceAudience = search.audience ?? 'sales';
  // The blocks' switch; the section's tier gate is `access`.
  const questionsSwitch = useQuestionsSwitch(sessionShell, lockedDemo);
  // The theme set (#845) — behind its own flag (beta, Pro and up).
  const themesAvailable = useVoiceThemesAvailable();
  // The set's panel is opened by the trigger in the header («Themes», or «Set
  // themes» for an empty set) AND by the themes block's proposals line, so the
  // section holds the state.
  const [themesOpen, setThemesOpen] = useState(false);
  // Why the theme filter was lifted — said until dismissed or a theme is picked.
  const [themeRefusal, setThemeRefusal] = useState<VoiceThemeFilterErrorCode | null>(
    null
  );
  // The selected theme as of the last render: a refusal arriving later — a
  // FAQ draft started under a theme since replaced — names a theme the page no
  // longer shows and lifts nothing.
  const selectedTheme = useRef(search.voiceTheme);
  // A layout effect: it runs before the tabs' passive effects of the same
  // commit, which report a refusal of the theme just selected.
  useLayoutEffect(() => {
    selectedTheme.current = search.voiceTheme;
  }, [search.voiceTheme]);
  const onThemeRefused = useCallback(
    (code: VoiceThemeFilterErrorCode, themeCode: string) => {
      if (themeCode !== selectedTheme.current) return;
      setThemeRefusal(code);
      // The row and code the theme expanded leave with it, as on «Clear» (#2238).
      onSearchChange(themeFilterPatch(undefined));
    },
    [onSearchChange]
  );

  if (access === 'loading') return <VoiceLoading />;
  // A failed check is not a yes: no tab opens on it — not even Sales and
  // Site, which carry no paid data — until the check says so (#1379).
  if (access === 'failed') {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border bg-bg-surface px-4 py-8 text-center">
        <p className="text-sm font-medium text-fg">{t('voicePage.accessError.title')}</p>
        <p className="max-w-prose text-sm text-fg-muted">
          {t('voicePage.accessError.body')}
        </p>
        {retryAccess && (
          <Button variant="outline" size="sm" onClick={retryAccess}>
            {t('voicePage.accessError.retry')}
          </Button>
        )}
      </div>
    );
  }
  // Not open: `VoiceLocked` says why — a plan below Pro on the shared tier
  // screen (#1897), a switched-off section and the shell that cannot tell the
  // plan from a switch (`unavailable`, Bitrix24) in their own words.
  if (access !== 'enabled')
    return <VoiceLocked code={access} upgradeHref={upgradeHref} />;

  return (
    <VoiceThemeRefusalContext.Provider value={onThemeRefused}>
      <VoiceSeriesScope search={search} onSearchChange={onSearchChange}>
        {lockedDemo && (
          <VoiceDemoBanner
            search={search}
            onSearchChange={onSearchChange}
            audience={audience}
          />
        )}
        <Tabs
          value={audience}
          // A tab change drops the expanded row, objection code, section and cut:
          // they name something of the tab being left.
          onValueChange={(value) => {
            if (!isVoiceAudience(value)) return;
            onSearchChange({
              audience: value === 'sales' ? undefined : value,
              row: undefined,
              code: undefined,
              voiceSection: undefined,
              voiceCut: undefined,
            });
          }}
          className="space-y-4"
        >
          <div className="flex flex-wrap items-end justify-between gap-2">
            {/* A tab's first answer starts on intent — the pointer resting on its
            trigger, or focus — not on the click (#2316). Mounted here, behind the
            section's gate: a closed or unchecked screen asks nothing. The
            questions tabs ask only when their own query would. */}
            <VoiceAudienceTabList
              search={search}
              onSearchChange={onSearchChange}
              questionsOn={questionsSwitch === 'on' || questionsSwitch === 'unknown'}
            />
            {/* The organization's theme set (#845) — behind the section's own
            gate above AND its own flag (`enableConversationThemes`: on by tier
            default from Pro up, the organization can switch it off). */}
            {themesAvailable && (
              <div className="flex flex-wrap items-center gap-2">
                {/* The filter reads the open tab's answer of the themes block. */}
                <VoiceThemeFilterPicker
                  search={search}
                  onSearchChange={onSearchChange}
                  // The open tab's answer of the themes block: its cut on Sales
                  // and Site, the rows alone on the other tabs.
                  cut={
                    audience === 'site'
                      ? 'questions'
                      : audience === 'sales'
                        ? 'outcomes'
                        : undefined
                  }
                  onChange={(voiceTheme) => {
                    setThemeRefusal(null);
                    onSearchChange(themeFilterPatch(voiceTheme));
                  }}
                />
                <VoiceThemesSheet
                  onOpenCall={onOpenCall}
                  open={themesOpen}
                  onOpenChange={setThemesOpen}
                />
              </div>
            )}
          </div>

          {themeRefusal !== null && (
            <Alert variant="info" dismissible onDismiss={() => setThemeRefusal(null)}>
              <AlertIcon />
              <AlertDescription>
                {t(
                  themeRefusal === 'themes_disabled'
                    ? 'voicePage.themeFilter.disabled'
                    : 'voicePage.themeFilter.invalid'
                )}
              </AlertDescription>
              <AlertClose aria-label={t('voicePage.themeFilter.dismiss')} />
            </Alert>
          )}

          <TabsContent value="sales">
            <VoiceSalesTab
              search={search}
              onSearchChange={onSearchChange}
              onOpenCall={onOpenCall}
              questionsSwitch={questionsSwitch}
              onOpenThemes={() => setThemesOpen(true)}
              upgradeHref={upgradeHref}
            />
          </TabsContent>
          {(['marketing', 'product'] as const).map((id) => (
            <TabsContent key={id} value={id}>
              <VoiceAudienceTab
                audience={id}
                search={search}
                onSearchChange={onSearchChange}
                onOpenCall={onOpenCall}
                upgradeHref={upgradeHref}
              />
            </TabsContent>
          ))}
          <TabsContent value="site">
            <VoiceSiteTab
              search={search}
              onSearchChange={onSearchChange}
              onOpenCall={onOpenCall}
              questionsSwitch={questionsSwitch}
              upgradeHref={upgradeHref}
              onOpenThemes={() => setThemesOpen(true)}
            />
          </TabsContent>
        </Tabs>
      </VoiceSeriesScope>
    </VoiceThemeRefusalContext.Provider>
  );
}
